// Preloading utilities for critical pages and components

// Critical pages that should be preloaded
const criticalPages = {
  HomePage: () => import('../pages/HomePage'),
  DashboardPage: () => import('../pages/DashboardPage'),
  AssessmentPage: () => import('../pages/AssessmentPage'),
  ChatPage: () => import('../pages/ChatPage'),
  LoginPage: () => import('../pages/LoginPage'),
  RegisterPage: () => import('../pages/RegisterPage'),
  AcademyPage: () => import('../pages/AcademyPage'),
  FreeModule: () => import('../pages/FreeModule'),
  SingleModule: () => import('../pages/SingleModule'),
  NextSteps: () => import('../pages/NextSteps'),
  UIBasics: () => import('../pages/UIBasics'),
} as const;

// Preload critical pages
export const preloadCriticalPages = () => {
  // Use timeout to preload after initial render
  setTimeout(() => {
    console.log('🚀 Starting critical pages preloading...');
    Object.entries(criticalPages).forEach(([pageName, pageImporter], index) => {
      // Stagger preloading to avoid bandwidth spikes
      setTimeout(() => {
        console.log(`📄 Preloading: ${pageName}`);
        pageImporter();
      }, index * 100);
    });
  }, 200);
};

// Preload page on hover for instant navigation
export const preloadPageOnHover = (pageName: keyof typeof criticalPages) => {
  return () => {
    if (criticalPages[pageName]) {
      criticalPages[pageName]();
    }
  };
};

// Preload next likely page based on current route
export const preloadNextLikelyPage = (currentPath: string) => {
  const preloadMap: Record<string, keyof typeof criticalPages> = {
    '/': 'AcademyPage', // From home to academy
    '/register': 'DashboardPage', // After registration
    '/login': 'DashboardPage', // After login
    '/dashboard': 'AssessmentPage', // From dashboard to assessments
    '/assessments': 'FreeModule', // From assessments to new assessment
    '/academy': 'FreeModule', // From academy to free module
    '/academy/free-module': 'NextSteps', // After completing assessment
  };

  const nextPage = preloadMap[currentPath];
  if (nextPage && criticalPages[nextPage]) {
    console.log(`🎯 Preloading next likely page: ${nextPage} for route: ${currentPath}`);
    criticalPages[nextPage]();
  }
};

// Resource hints for critical resources
export const addResourceHints = () => {
  const head = document.head;
  
  // Preconnect to external domains
  const preconnects = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://api.openai.com',
  ];

  preconnects.forEach(href => {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      head.appendChild(link);
    }
  });

  // DNS prefetch for other domains
  const dnsPrefetch = [
    'https://cdnjs.cloudflare.com',
  ];

  dnsPrefetch.forEach(href => {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = href;
      head.appendChild(link);
    }
  });
};

// Preload critical fonts
export const preloadFonts = () => {
  const fonts = [
    'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA.woff2',
  ];

  fonts.forEach(href => {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.type = 'font/woff2';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  });
};

// Initialize all preloading strategies
export const initializePreloading = () => {
  addResourceHints();
  preloadFonts();
  preloadCriticalPages();
};